<script>
import { useAlert, useTrack } from 'dashboard/composables';
import { mapGetters, mapActions } from 'vuex';
import ReportFilterSelector from './components/FilterSelector.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import kanbanAPI from '../../../../api/kanban';
import funnelAPI from '../../../../api/funnel';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { generateFileName } from '../../../../helper/downloadHelper';
import KanbanMetrics from './components/KanbanMetrics.vue';
import StageDistributionChart from './components/StageDistributionChart.vue';
import StatusDistributionChart from './components/StatusDistributionChart.vue';
import ValueByPriorityChart from './components/ValueByPriorityChart.vue';
import ValueByStatusChart from './components/ValueByStatusChart.vue';
import TopAgentsList from './components/TopAgentsList.vue';
import NoAgentCard from './components/NoAgentCard.vue';
import MaxMinValueCard from './components/MaxMinValueCard.vue';

export default {
  name: 'KanbanReports',
  components: {
    ReportFilterSelector,
    V4Button,
    ReportHeader,
    KanbanMetrics,
    StageDistributionChart,
    StatusDistributionChart,
    ValueByPriorityChart,
    ValueByStatusChart,
    TopAgentsList,
    NoAgentCard,
    MaxMinValueCard,
  },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      from: thirtyDaysAgo.getTime(),
      to: today.getTime(),
      userIds: [],
      inbox: null,
      metrics: {
        totalItems: 0,
        itemsByStage: {},
        averageValue: 0,
        totalValue: 0,
        avgTimeInStage: {},
        conversionRates: {},
        stageMetrics: {
          valueByStage: {},
          itemsCreatedToday: 0,
          itemsCreatedThisWeek: 0,
          itemsCreatedThisMonth: 0,
          stageVelocity: {},
          avgTimeToConversion: {},
          stageEfficiency: {},
          itemsWithDeadline: 0,
          itemsWithRescheduling: 0,
          itemsWithOffers: 0,
          avgOffersValue: 0,
          totalOffers: 0,
          offerRanges: {
            low: 0, // Até R$ 1.000
            medium: 0, // R$ 1.001 a R$ 5.000
            high: 0, // Acima de R$ 5.000
          },
          priorityDistribution: {
            low: 0,
            medium: 0,
            high: 0,
          },
          channelDistribution: {},
        },
        funnelMetrics: {
          totalFunnels: 0,
          activeStages: 0,
          stageDistribution: {},
          averageStagesPerFunnel: 0,
          conversionByStage: {},
          timeInStageByFunnel: {},
          valueByStage: {},
          stageColors: {},
          messageTemplates: {},
          stagePositions: {},
        },
        checklistMetrics: {
          totalTasks: 0,
          completedTasks: 0,
          completionRate: 0,
          itemsWithChecklists: 0,
          averageTasksPerItem: 0,
        },
        activityMetrics: {
          totalActivities: 0,
          activitiesByType: {},
          averageActivitiesPerItem: 0,
          itemsWithNotes: 0,
          itemsWithAttachments: 0,
          stageChanges: 0,
          valueChanges: 0,
          agentChanges: 0,
          itemsWithConversations: 0,
        },
        contactMetrics: {
          totalContacts: new Set(),
          contactsWithEmail: 0,
          contactsWithPhone: 0,
          contactsWithBoth: 0,
        },
      },
      isLoading: false,
      funnels: [],
      maxHourlyChanges: 0,
      items: [],
    };
  },
  computed: {
    ...mapGetters('funnel', ['getFunnels', 'getSelectedFunnel', 'getUIFlags']),
    selectedFunnel() {
      return this.getSelectedFunnel;
    },
    funnelsFromStore() {
      return this.getFunnels;
    },
    requestPayload() {
      const payload = {};
      if (this.from) payload.from = this.from;
      if (this.to) payload.to = this.to;
      if (this.userIds && this.userIds.length > 0) {
        // Extrair apenas os IDs dos agentes, não os objetos completos
        payload.user_ids = this.userIds.map(agent =>
          typeof agent === 'object' ? agent.id : agent
        );
      }
      if (this.inbox) payload.inbox_id = this.inbox;
      if (this.selectedFunnel?.id) {
        // O selectedFunnel já é o objeto do funil, usar diretamente o ID
        payload.funnel_id = this.selectedFunnel.id;
      }
      return payload;
    },
    // Helper para garantir que items seja sempre um array
    safeItems() {
      return Array.isArray(this.items) ? this.items : [];
    },
    defaultChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        animation: {
          duration: 750,
          easing: 'easeInOutQuart',
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                family: "'Inter', sans-serif",
                size: 12,
              },
              color: '#94a3b8',
            },
          },
          tooltip: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            titleColor: '#e2e8f0',
            bodyColor: '#e2e8f0',
            borderColor: '#334155',
            borderWidth: 1,
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            usePointStyle: true,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toLocaleString();
                }
                return label;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(51, 65, 85, 0.1)',
              drawBorder: false,
            },
            ticks: {
              color: '#94a3b8',
              padding: 10,
              font: {
                family: "'Inter', sans-serif",
                size: 11,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#94a3b8',
              padding: 10,
              font: {
                family: "'Inter', sans-serif",
                size: 11,
              },
            },
          },
        },
      };
    },
    lineChartOptions() {
      return {
        ...this.defaultChartOptions,
        plugins: {
          ...this.defaultChartOptions.plugins,
          tooltip: {
            ...this.defaultChartOptions.plugins.tooltip,
            intersect: false,
            mode: 'nearest',
          },
        },
        elements: {
          line: {
            tension: 0.4,
          },
          point: {
            radius: 4,
            hoverRadius: 6,
            backgroundColor: '#fff',
            borderWidth: 3,
          },
        },
      };
    },
    pieChartOptions() {
      return {
        ...this.defaultChartOptions,
        cutout: '60%',
        plugins: {
          ...this.defaultChartOptions.plugins,
          tooltip: {
            ...this.defaultChartOptions.plugins.tooltip,
            callbacks: {
              label: context => {
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value * 100) / total).toFixed(1);
                return `${
                  context.label
                }: ${value.toLocaleString()} (${percentage}%)`;
              },
            },
          },
        },
      };
    },
    barChartOptions() {
      return {
        ...this.defaultChartOptions,
        plugins: {
          ...this.defaultChartOptions.plugins,
          tooltip: {
            ...this.defaultChartOptions.plugins.tooltip,
            callbacks: {
              label: context => {
                return `${context.dataset.label}: ${this.formatCurrency(
                  context.parsed.y
                )}`;
              },
            },
          },
        },
        scales: {
          ...this.defaultChartOptions.scales,
          y: {
            ...this.defaultChartOptions.scales.y,
            ticks: {
              ...this.defaultChartOptions.scales.y.ticks,
              callback: value => this.formatCurrency(value),
            },
          },
        },
      };
    },
    radarChartOptions() {
      return {
        ...this.defaultChartOptions,
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            ticks: {
              stepSize: 20,
              color: '#94a3b8',
              backdropColor: 'transparent',
            },
            grid: {
              color: 'rgba(51, 65, 85, 0.1)',
            },
            angleLines: {
              color: 'rgba(51, 65, 85, 0.1)',
            },
            pointLabels: {
              color: '#94a3b8',
              font: {
                family: "'Inter', sans-serif",
                size: 11,
              },
            },
          },
        },
        plugins: {
          ...this.defaultChartOptions.plugins,
          tooltip: {
            ...this.defaultChartOptions.plugins.tooltip,
            callbacks: {
              label: context => {
                return `${context.dataset.label}: ${context.parsed}%`;
              },
            },
          },
        },
      };
    },
    lineChartData() {
      const stages = Object.keys(this.metrics.itemsByStage);
      const sortedStages = stages.sort((a, b) => {
        return (
          (this.metrics.funnelMetrics.stagePositions[a] || 0) -
          (this.metrics.funnelMetrics.stagePositions[b] || 0)
        );
      });

      return {
        labels: sortedStages,
        datasets: [
          {
            label: 'Itens por Estágio',
            data: sortedStages.map(stage =>
              Number(this.metrics.itemsByStage[stage] || 0)
            ),
            fill: true,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#3b82f6',
            pointHoverBackgroundColor: '#3b82f6',
            pointHoverBorderColor: '#fff',
          },
        ],
      };
    },
    pieChartData() {
      const stages = Object.keys(this.metrics.itemsByStage);
      return {
        labels: stages,
        datasets: [
          {
            data: stages.map(stage =>
              Number(this.metrics.itemsByStage[stage] || 0)
            ),
            backgroundColor: stages.map(
              stage =>
                this.metrics.funnelMetrics.stageColors[stage] || '#94a3b8'
            ),
            borderWidth: 2,
            borderColor: '#1e293b',
            hoverOffset: 15,
          },
        ],
      };
    },
    barChartData() {
      const stages = Object.keys(this.metrics.stageMetrics.valueByStage);
      const sortedStages = stages.sort((a, b) => {
        return (
          (this.metrics.funnelMetrics.stagePositions[a] || 0) -
          (this.metrics.funnelMetrics.stagePositions[b] || 0)
        );
      });

      return {
        labels: sortedStages,
        datasets: [
          {
            label: 'Valor por Estágio',
            data: sortedStages.map(stage =>
              Number(this.metrics.stageMetrics.valueByStage[stage] || 0)
            ),
            backgroundColor: 'rgba(16, 185, 129, 0.2)',
            borderColor: '#10b981',
            borderWidth: 2,
            borderRadius: 6,
            hoverBackgroundColor: 'rgba(16, 185, 129, 0.4)',
          },
        ],
      };
    },
    radarChartData() {
      const stages = Object.keys(this.metrics.stageMetrics.stageEfficiency);
      return {
        labels: stages,
        datasets: [
          {
            label: 'Eficiência de Estágio',
            data: stages.map(stage =>
              Math.round(
                (this.metrics.stageMetrics.stageEfficiency[stage] || 0) * 100
              )
            ),
            backgroundColor: 'rgba(139, 92, 246, 0.2)',
            borderColor: '#8b5cf6',
            borderWidth: 2,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#8b5cf6',
            pointHoverBackgroundColor: '#8b5cf6',
            pointHoverBorderColor: '#fff',
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      };
    },
    calendarData() {
      const today = new Date();
      const data = [];
      let maxValue = 0;

      // Gerar dados dos últimos 365 dias
      for (let i = 0; i < 365; i += 1) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const value = Math.floor(Math.random() * 5); // Valor entre 0 e 5 para simular atividades
        maxValue = Math.max(maxValue, value);
        data.push({
          day: date.toISOString().split('T')[0],
          value: Number(value),
        });
      }

      return data;
    },
    statusChartData() {
      // Status: Aberto, Ganho, Perdido
      const statusMap = { open: 0, won: 0, lost: 0 };
      this.safeItems.forEach(item => {
        const status = item.item_details?.status || 'open';
        if (statusMap[status] !== undefined) statusMap[status]++;
      });
      return {
        labels: ['Aberto', 'Ganho', 'Perdido'],
        values: [statusMap.open, statusMap.won, statusMap.lost],
        colors: ['#94a3b8', '#22c55e', '#ef4444'],
      };
    },
    valueByPriorityChartData() {
      // Prioridades: urgent, high, medium, low, none
      const prioList = [
        { key: 'urgent', label: 'Urgente', color: '#ef4444' },
        { key: 'high', label: 'Alta', color: '#a21caf' },
        { key: 'medium', label: 'Média', color: '#f59e0b' },
        { key: 'low', label: 'Baixa', color: '#22c55e' },
        { key: 'none', label: 'Nenhuma', color: '#94a3b8' },
      ];
      const valueMap = {};
      const countMap = {};
      this.safeItems.forEach(item => {
        const prio = item.item_details?.priority || 'none';
        valueMap[prio] =
          (valueMap[prio] || 0) + (parseFloat(item.item_details?.value) || 0);
        countMap[prio] = (countMap[prio] || 0) + 1;
      });
      const total = Object.values(countMap).reduce((a, b) => a + b, 0) || 1;
      return {
        labels: prioList.map(p => p.label),
        values: prioList.map(p => valueMap[p.key] || 0),
        colors: prioList.map(p => p.color),
        percents: prioList.map(p =>
          (((countMap[p.key] || 0) / total) * 100).toFixed(1)
        ),
      };
    },
    valueByStatusChartData() {
      // Status: open, won, lost
      const statusList = [
        { key: 'open', label: 'Aberto', color: '#94a3b8' },
        { key: 'won', label: 'Ganho', color: '#22c55e' },
        { key: 'lost', label: 'Perdido', color: '#ef4444' },
      ];
      const valueMap = {};
      const countMap = {};
      this.safeItems.forEach(item => {
        const status = item.item_details?.status || 'open';
        valueMap[status] =
          (valueMap[status] || 0) + (parseFloat(item.item_details?.value) || 0);
        countMap[status] = (countMap[status] || 0) + 1;
      });
      const total = Object.values(countMap).reduce((a, b) => a + b, 0) || 1;
      return {
        labels: statusList.map(s => s.label),
        values: statusList.map(s => valueMap[s.key] || 0),
        colors: statusList.map(s => s.color),
        percents: statusList.map(s =>
          (((countMap[s.key] || 0) / total) * 100).toFixed(1)
        ),
      };
    },
    topAgents() {
      // Top 5 agentes por quantidade de cards
      const map = new Map();
      this.safeItems.forEach(item => {
        const agent = item.item_details?.agent;
        if (agent && agent.id) {
          if (!map.has(agent.id)) {
            map.set(agent.id, { ...agent, count: 1 });
          } else {
            map.get(agent.id).count++;
          }
        }
      });
      return Array.from(map.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    },
    noAgentData() {
      // Cards sem agente
      const items = this.safeItems.filter(item => !item.item_details?.agent);
      const totalValue = items.reduce(
        (sum, item) => sum + (parseFloat(item.item_details?.value) || 0),
        0
      );
      return { count: items.length, totalValue };
    },
    maxMinValueData() {
      // Card de maior e menor valor
      const itemsWithValue = this.safeItems.filter(
        item => parseFloat(item.item_details?.value) > 0
      );
      if (!itemsWithValue.length) return { maxItem: null, minItem: null };
      const maxItem = itemsWithValue.reduce(
        (max, item) =>
          parseFloat(item.item_details?.value) >
          parseFloat(max?.item_details?.value || 0)
            ? item
            : max,
        null
      );
      const minItem = itemsWithValue.reduce(
        (min, item) =>
          parseFloat(item.item_details?.value) <
          parseFloat(min?.item_details?.value || Infinity)
            ? item
            : min,
        null
      );
      return { maxItem, minItem };
    },
    deadlineCardData() {
      const itemsWithDeadline = this.safeItems.filter(
        item => item.item_details?.deadline_at
      );
      const total = itemsWithDeadline.length;
      const totalValue = itemsWithDeadline.reduce(
        (sum, item) => sum + (parseFloat(item.item_details?.value) || 0),
        0
      );
      const avgValue = total ? totalValue / total : 0;
      const nextDeadline = itemsWithDeadline
        .map(item => new Date(item.item_details.deadline_at))
        .filter(date => !isNaN(date))
        .sort((a, b) => a - b)[0];
      const priorities = {};
      itemsWithDeadline.forEach(item => {
        const prio = item.item_details?.priority || 'none';
        priorities[prio] = (priorities[prio] || 0) + 1;
      });
      const mostCommonPriority =
        Object.entries(priorities).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
      return {
        total,
        totalValue,
        avgValue,
        nextDeadline: nextDeadline
          ? nextDeadline.toLocaleDateString('pt-BR')
          : '-',
        mostCommonPriority,
        percent: this.metrics.totalItems
          ? ((total / this.metrics.totalItems) * 100).toFixed(1)
          : '0',
      };
    },
    offersCardData() {
      const itemsWithOffers = this.safeItems.filter(
        item =>
          item.item_details?.offers &&
          Array.isArray(item.item_details.offers) &&
          item.item_details.offers.length > 0
      );
      const total = itemsWithOffers.length;
      let totalOffersValue = 0;
      let offerCount = 0;
      let maxOffer = null;
      let minOffer = null;
      let multiOfferCount = 0;
      const priorities = {};
      itemsWithOffers.forEach(item => {
        if (item.item_details.offers.length > 1) multiOfferCount++;
        const prio = item.item_details?.priority || 'none';
        priorities[prio] = (priorities[prio] || 0) + 1;
        item.item_details.offers.forEach(offer => {
          if (offer.value !== undefined) {
            totalOffersValue += offer.value;
            offerCount++;
            if (maxOffer === null || offer.value > maxOffer)
              maxOffer = offer.value;
            if (minOffer === null || offer.value < minOffer)
              minOffer = offer.value;
          }
        });
      });
      const avgOfferValue = offerCount ? totalOffersValue / offerCount : 0;
      const mostCommonPriority =
        Object.entries(priorities).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
      return {
        total,
        avgOfferValue,
        totalOffersValue,
        maxOffer,
        minOffer,
        multiOfferCount,
        mostCommonPriority,
        percent: this.metrics.totalItems
          ? ((total / this.metrics.totalItems) * 100).toFixed(1)
          : '0',
      };
    },
  },
  watch: {
    selectedFunnel: {
      handler(newFunnel, oldFunnel) {
        // Se o funil mudou e temos um funil válido, recarregar os dados
        if (newFunnel?.id && newFunnel.id !== oldFunnel?.id) {
          this.fetchAllData();
        }
      },
      immediate: false,
    },
  },
  async mounted() {
    // Primeiro, garantir que temos os funis carregados e um selecionado
    await this.ensureFunnelSelected();
    this.fetchAllData();
  },
  methods: {
    ...mapActions('funnel', ['fetch', 'setSelectedFunnel']),
    async ensureFunnelSelected() {
      try {
        // Se não temos funis, buscar do store
        if (!this.funnelsFromStore || this.funnelsFromStore.length === 0) {
          await this.fetch();
        }

        // Se ainda não temos um funil selecionado, selecionar o primeiro disponível
        if (
          !this.selectedFunnel &&
          this.funnelsFromStore &&
          this.funnelsFromStore.length > 0
        ) {
          const firstFunnel = this.funnelsFromStore[0];
          await this.setSelectedFunnel(firstFunnel);
        }

        // Verificar se o funil selecionado ainda é válido
        if (this.selectedFunnel && this.funnelsFromStore) {
          const funnelStillExists = this.funnelsFromStore.some(
            f => String(f.id) === String(this.selectedFunnel.id)
          );
          if (!funnelStillExists) {
            // Se o funil selecionado não existe mais, selecionar o primeiro disponível
            if (this.funnelsFromStore.length > 0) {
              const firstFunnel = this.funnelsFromStore[0];
              await this.setSelectedFunnel(firstFunnel);
            } else {
              throw new Error('Nenhum funil disponível para análise');
            }
          }
        }

        // Se não conseguimos selecionar um funil, mostrar erro
        if (!this.selectedFunnel) {
          throw new Error('Nenhum funil disponível para análise');
        }
      } catch (error) {
        console.error('Erro ao garantir funil selecionado:', error);
        try {
          const alert = useAlert();
          if (alert && alert.showAlert) {
            alert.showAlert(
              'Erro ao carregar funis. Verifique se existem funis configurados.'
            );
          }
        } catch (alertError) {
          console.error('Erro ao mostrar alerta:', alertError);
        }
      }
    },
    async fetchAllData() {
      try {
        this.isLoading = true;
        // Garantir que temos um funil selecionado antes de buscar dados
        await this.ensureFunnelSelected();

        // Verificação final antes de prosseguir
        if (!this.selectedFunnel?.id) {
          throw new Error('Nenhum funil selecionado para análise');
        }

        await Promise.all([this.fetchMetrics(), this.fetchFunnels()]);
      } catch (error) {
        console.error('Erro ao carregar métricas do Kanban:', error);
        // Usar try-catch para evitar erro de destructuring
        try {
          const alert = useAlert();
          if (alert && alert.showAlert) {
            alert.showAlert('Erro ao carregar métricas do Kanban');
          }
        } catch (alertError) {
          console.error('Erro ao mostrar alerta:', alertError);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchFunnels() {
      try {
        // Usar os funis do store em vez de fazer chamada direta à API
        if (this.funnelsFromStore && this.funnelsFromStore.length > 0) {
          this.funnels = this.funnelsFromStore;
        } else {
          // Se não temos no store, buscar
          await this.fetch();
          this.funnels = this.funnelsFromStore || [];
        }

        this.calculateFunnelMetrics();
      } catch (error) {
        console.error('Erro ao carregar dados dos funis:', error);
        try {
          const alert = useAlert();
          if (alert && alert.showAlert) {
            alert.showAlert('Erro ao carregar dados dos funis');
          }
        } catch (alertError) {
          console.error('Erro ao mostrar alerta:', alertError);
        }
      }
    },
    calculateFunnelMetrics() {
      const funnelMetrics = {
        totalFunnels: this.funnels.length,
        activeStages: 0,
        stageDistribution: {},
        averageStagesPerFunnel: 0,
        conversionByStage: {},
        timeInStageByFunnel: {},
        valueByStage: {},
        stageColors: {},
        messageTemplates: {},
        stagePositions: {},
      };

      this.funnels.forEach(funnel => {
        if (funnel.stages && typeof funnel.stages === 'object') {
          const stages = Object.values(funnel.stages);
          funnelMetrics.activeStages += stages.length;

          // Distribuição de estágios e cores
          stages.forEach(stage => {
            if (stage && stage.name) {
              funnelMetrics.stageDistribution[stage.name] =
                (funnelMetrics.stageDistribution[stage.name] || 0) + 1;
              funnelMetrics.stageColors[stage.name] = stage.color || '#94a3b8';
              funnelMetrics.stagePositions[stage.name] = stage.position || 0;

              // Contagem de templates de mensagem por estágio
              if (
                stage.message_templates &&
                Array.isArray(stage.message_templates)
              ) {
                funnelMetrics.messageTemplates[stage.name] =
                  (funnelMetrics.messageTemplates[stage.name] || 0) +
                  stage.message_templates.length;
              }
            }
          });
        }
      });

      // Média de estágios por funil
      funnelMetrics.averageStagesPerFunnel =
        funnelMetrics.totalFunnels > 0
          ? funnelMetrics.activeStages / funnelMetrics.totalFunnels
          : 0;

      this.metrics.funnelMetrics = funnelMetrics;
    },
    async fetchMetrics() {
      try {
        // Verificar se temos um funil selecionado
        if (!this.selectedFunnel?.id) {
          throw new Error('Nenhum funil selecionado para análise');
        }

        // Usar o funil selecionado do store - enviar apenas parâmetros permitidos
        const params = {};
        if (this.selectedFunnel?.id) {
          // O selectedFunnel já é o objeto do funil, usar diretamente o ID
          params.funnel_id = this.selectedFunnel.id;
        }
        if (this.from) params.from = this.from;
        if (this.to) params.to = this.to;
        if (this.userIds && this.userIds.length > 0)
          params.user_ids = this.userIds;
        if (this.inbox) params.inbox_id = this.inbox;

        console.log('[DEBUG] Parâmetros para API:', params);
        console.log('[DEBUG] selectedFunnel:', this.selectedFunnel);

        // A API espera funnelId como primeiro parâmetro, não como objeto params
        // Extrair apenas o ID do agente se houver um selecionado
        let agentId = null;
        if (this.userIds && this.userIds.length > 0) {
          const agent = this.userIds[0];
          agentId = typeof agent === 'object' ? agent.id : agent;
        }

        const response = await kanbanAPI.getItems(
          params.funnel_id,
          1,
          null,
          agentId
        );

        // Corrigir acesso aos dados - a API retorna {items, pagination}
        const items = response.data?.items || [];
        this.items = items;
        this.metrics = {
          ...this.metrics,
          ...this.calculateMetrics(items),
        };
      } catch (error) {
        console.error('Erro ao carregar métricas do Kanban:', error);
        try {
          const alert = useAlert();
          if (alert && alert.showAlert) {
            alert.showAlert('Erro ao carregar métricas do Kanban');
          }
        } catch (alertError) {
          console.error('Erro ao mostrar alerta:', alertError);
        }
      }
    },
    calculateMetrics(items) {
      if (!Array.isArray(items)) {
        console.warn('calculateMetrics recebeu dados inválidos:', items);
        return this.metrics;
      }

      const metrics = {
        totalItems: items.length,
        itemsByStage: {},
        totalValue: 0,
        averageValue: 0,
        avgTimeInStage: {},
        conversionRates: {},
        stageMetrics: {
          valueByStage: {},
          itemsCreatedToday: 0,
          itemsCreatedThisWeek: 0,
          itemsCreatedThisMonth: 0,
          stageVelocity: {},
          avgTimeToConversion: {},
          stageEfficiency: {},
          itemsWithDeadline: 0,
          itemsWithRescheduling: 0,
          itemsWithOffers: 0,
          avgOffersValue: 0,
          totalOffers: 0,
          offerRanges: {
            low: 0, // Até R$ 1.000
            medium: 0, // R$ 1.001 a R$ 5.000
            high: 0, // Acima de R$ 5.000
          },
          priorityDistribution: {
            low: 0,
            medium: 0,
            high: 0,
          },
          channelDistribution: {},
        },
        checklistMetrics: {
          totalTasks: 0,
          completedTasks: 0,
          completionRate: 0,
          itemsWithChecklists: 0,
          averageTasksPerItem: 0,
        },
        activityMetrics: {
          totalActivities: 0,
          activitiesByType: {},
          averageActivitiesPerItem: 0,
          itemsWithNotes: 0,
          itemsWithAttachments: 0,
          stageChanges: 0,
          valueChanges: 0,
          agentChanges: 0,
          itemsWithConversations: 0,
        },
        contactMetrics: {
          totalContacts: new Set(),
          contactsWithEmail: 0,
          contactsWithPhone: 0,
          contactsWithBoth: 0,
        },
      };

      const today = new Date();
      const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Calcular métricas básicas e avançadas
      items.forEach(item => {
        if (!item) return; // Pular itens nulos/undefined

        const stage = item.funnel_stage || 'unknown';
        const details = item.item_details || {};
        const value = parseFloat(details?.value) || 0;
        const createdAt = new Date(item.created_at);
        const checklist = Array.isArray(details?.checklist)
          ? details.checklist
          : [];
        const activities = Array.isArray(details?.activities)
          ? details.activities
          : [];

        // Contagem por estágio
        metrics.itemsByStage[stage] = (metrics.itemsByStage[stage] || 0) + 1;

        // Valor total e por estágio
        metrics.totalValue += value;
        metrics.stageMetrics.valueByStage[stage] =
          (metrics.stageMetrics.valueByStage[stage] || 0) + value;

        // Contagem de itens recentes
        if (createdAt >= today.setHours(0, 0, 0, 0)) {
          metrics.stageMetrics.itemsCreatedToday += 1;
        }
        if (createdAt >= thisWeek) {
          metrics.stageMetrics.itemsCreatedThisWeek += 1;
        }
        if (createdAt >= thisMonth) {
          metrics.stageMetrics.itemsCreatedThisMonth += 1;
        }

        // Métricas de checklist
        if (checklist.length > 0) {
          metrics.checklistMetrics.itemsWithChecklists += 1;
          metrics.checklistMetrics.totalTasks += checklist.length;
          metrics.checklistMetrics.completedTasks += checklist.filter(
            task => task && task.completed
          ).length;
        }

        // Métricas de atividades
        if (activities.length > 0) {
          metrics.activityMetrics.totalActivities += activities.length;

          activities.forEach(activity => {
            if (activity && activity.type) {
              metrics.activityMetrics.activitiesByType[activity.type] =
                (metrics.activityMetrics.activitiesByType[activity.type] || 0) +
                1;

              if (activity.type === 'stage_changed') {
                metrics.activityMetrics.stageChanges += 1;
              } else if (activity.type === 'value_changed') {
                metrics.activityMetrics.valueChanges += 1;
              } else if (activity.type === 'note_added') {
                metrics.activityMetrics.itemsWithNotes += 1;
              } else if (activity.type === 'attachment_added') {
                metrics.activityMetrics.itemsWithAttachments += 1;
              } else if (activity.type === 'agent_changed') {
                metrics.activityMetrics.agentChanges += 1;
              }
            }
          });
        }

        // Métricas de prioridade
        if (details.priority) {
          const priority = details.priority;
          if (
            metrics.stageMetrics.priorityDistribution[priority] !== undefined
          ) {
            metrics.stageMetrics.priorityDistribution[priority] += 1;
          }
        }

        // Métricas de canal
        if (details.channel) {
          metrics.stageMetrics.channelDistribution[details.channel] =
            (metrics.stageMetrics.channelDistribution[details.channel] || 0) +
            1;
        }

        // Métricas de deadline e agendamento
        if (details.deadline_at) metrics.stageMetrics.itemsWithDeadline += 1;
        if (details.rescheduled)
          metrics.stageMetrics.itemsWithRescheduling += 1;

        // Métricas de ofertas
        if (
          details.offers &&
          Array.isArray(details.offers) &&
          details.offers.length > 0
        ) {
          metrics.stageMetrics.itemsWithOffers += 1;
          metrics.stageMetrics.totalOffers += details.offers.length;

          // Calcular valor total e médio das ofertas
          const offersTotal = details.offers.reduce(
            (sum, offer) => sum + (parseFloat(offer.value) || 0),
            0
          );
          metrics.stageMetrics.avgOffersValue =
            details.offers.length > 0 ? offersTotal / details.offers.length : 0;

          // Agrupar ofertas por faixa de valor
          details.offers.forEach(offer => {
            if (offer.value !== undefined) {
              const offerValue = parseFloat(offer.value);
              if (offerValue <= 1000) {
                metrics.stageMetrics.offerRanges.low += 1;
              } else if (offerValue <= 5000) {
                metrics.stageMetrics.offerRanges.medium += 1;
              } else {
                metrics.stageMetrics.offerRanges.high += 1;
              }
            }
          });
        }

        // Métricas de conversas
        if (item.conversation_display_id) {
          metrics.activityMetrics.itemsWithConversations += 1;
        }
      });

      // Calcular médias e taxas
      metrics.averageValue =
        items.length > 0 ? metrics.totalValue / items.length : 0;
      metrics.checklistMetrics.completionRate =
        metrics.checklistMetrics.totalTasks > 0
          ? metrics.checklistMetrics.completedTasks /
            metrics.checklistMetrics.totalTasks
          : 0;
      metrics.checklistMetrics.averageTasksPerItem =
        items.length > 0
          ? metrics.checklistMetrics.totalTasks / items.length
          : 0;
      metrics.activityMetrics.averageActivitiesPerItem =
        items.length > 0
          ? metrics.activityMetrics.totalActivities / items.length
          : 0;

      // Converter Set para número
      metrics.contactMetrics.totalContacts =
        metrics.contactMetrics.totalContacts.size;

      return metrics;
    },
    onFilterChange({
      from,
      to,
      groupBy,
      businessHours,
      selectedAgents,
      selectedLabel,
      selectedInbox,
      selectedTeam,
      selectedRating,
      selectedFunnel,
    } = {}) {
      if (from) this.from = from;
      if (to) this.to = to;
      if (selectedAgents) this.userIds = selectedAgents;
      if (selectedInbox) this.inbox = selectedInbox;

      // Se um funil específico foi selecionado no filtro, atualizar o store
      if (
        selectedFunnel &&
        selectedFunnel.id !== 'all' &&
        this.funnelsFromStore
      ) {
        const selectedFunnelFromStore = this.funnelsFromStore.find(
          f => String(f.id) === String(selectedFunnel.id)
        );
        if (selectedFunnelFromStore) {
          this.setSelectedFunnel(selectedFunnelFromStore);
        }
      } else if (selectedFunnel && selectedFunnel.id === 'all') {
        // Se "Todos os Funis" foi selecionado, usar o primeiro disponível
        if (this.funnelsFromStore && this.funnelsFromStore.length > 0) {
          this.setSelectedFunnel(this.funnelsFromStore[0]);
        }
      }

      try {
        const track = useTrack();
        if (track) {
          track(REPORTS_EVENTS.FILTER_REPORT, {
            filterType: 'kanban',
            filterValue: JSON.stringify(this.requestPayload),
          });
        }
      } catch (trackError) {
        console.error('Erro ao rastrear evento:', trackError);
      }

      // Aguardar um tick para garantir que o store foi atualizado antes de buscar dados
      this.$nextTick(() => {
        this.fetchAllData();
      });
    },
    async downloadReports() {
      try {
        // Verificar se temos um funil selecionado
        if (!this.selectedFunnel?.id) {
          throw new Error('Nenhum funil selecionado para download');
        }

        // Enviar apenas parâmetros permitidos
        const params = {};
        if (this.selectedFunnel?.id)
          params.funnel_id = String(this.selectedFunnel.id);
        if (this.from) params.from = this.from;
        if (this.to) params.to = this.to;
        if (this.userIds && this.userIds.length > 0)
          params.user_ids = this.userIds;
        if (this.inbox) params.inbox_id = this.inbox;

        const response = await kanbanAPI.getItems(params);
        const items = response.data?.items || [];
        const csvContent = this.convertToCSV(items);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = generateFileName('kanban_report');
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Erro ao baixar relatório do Kanban:', error);
        try {
          const alert = useAlert();
          if (alert && alert.showAlert) {
            alert.showAlert('Erro ao baixar relatório do Kanban');
          }
        } catch (alertError) {
          console.error('Erro ao mostrar alerta:', alertError);
        }
      }
    },
    convertToCSV(items) {
      if (!Array.isArray(items)) {
        console.warn('convertToCSV recebeu dados inválidos:', items);
        return '';
      }

      const headers = [
        'ID',
        'Estágio',
        'Título',
        'Valor',
        'Data de Criação',
        'Data de Entrada no Estágio',
        'Descrição',
        'Prioridade',
        'Canal',
        'ID do Contato',
        'Email',
        'Telefone',
        'Data Limite',
        'Total de Atividades',
        'Total de Notas',
        'Total de Anexos',
      ].join(',');

      const rows = items
        .map(item => {
          if (!item) return '';

          const details = item.item_details || {};
          const activities = Array.isArray(details.activities)
            ? details.activities
            : [];
          const notes = activities.filter(
            a => a && a.type === 'note_added'
          ).length;
          const attachments = activities.filter(
            a => a && a.type === 'attachment_added'
          ).length;

          return [
            item.id || '',
            item.funnel_stage || '',
            (details.title || '').replace(/,/g, ';'),
            details.value || '',
            item.created_at || '',
            item.stage_entered_at || '',
            (details.description || '').replace(/,/g, ';'),
            details.priority || '',
            details.channel || '',
            details.contact_id || '',
            details.email || '',
            details.phone || '',
            details.deadline_at || '',
            activities.length,
            notes,
            attachments,
          ].join(',');
        })
        .filter(row => row !== ''); // Remover linhas vazias

      return [headers, ...rows].join('\n');
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    },
    formatDuration(ms) {
      if (!ms || isNaN(ms)) return '0 dias';
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));
      return `${days} ${days === 1 ? 'dia' : 'dias'}`;
    },
    formatPercentage(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '0%';
      }
      return `${(value * 100).toFixed(1)}%`;
    },
    getHourlyChanges() {
      const hourlyChanges = Array(24).fill(0);
      const activities = Object.values(
        this.metrics.activityMetrics.activitiesByType
      ).reduce((acc, val) => acc + (val || 0), 0);

      // Distribuir as atividades ao longo do dia com uma curva natural
      for (let i = 0; i < activities; i += 1) {
        // Simular uma distribuição mais realista com pico durante horário comercial
        const hour = Math.floor((Math.sin(Math.random() * Math.PI) + 1) * 12);
        hourlyChanges[hour] = (hourlyChanges[hour] || 0) + 1;
      }

      this.maxHourlyChanges = Math.max(...hourlyChanges);
      return hourlyChanges;
    },
    getCompletionTrendClass(type = 'text') {
      const progress = this.getCompletionProgress();
      const baseClass = type === 'bg' ? 'bg-' : 'text-';

      if (progress >= 80)
        return `${baseClass}emerald-500 dark:${baseClass}emerald-400`;
      if (progress >= 50)
        return `${baseClass}yellow-500 dark:${baseClass}yellow-400`;
      return `${baseClass}red-500 dark:${baseClass}red-400`;
    },
    getCompletionProgress() {
      const totalItems = this.metrics.totalItems;
      const completedItems = Object.values(this.metrics.itemsByStage).reduce(
        (acc, val) => acc + (val || 0),
        0
      );
      return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    },
    getCompletionForecast() {
      const velocity = this.getWeeklyThroughput();
      const remainingItems =
        this.metrics.totalItems -
        Object.values(this.metrics.itemsByStage).reduce(
          (acc, val) => acc + (val || 0),
          0
        );
      const weeksToComplete = velocity > 0 ? remainingItems / velocity : 0;

      const date = new Date();
      date.setDate(date.getDate() + weeksToComplete * 7);

      return date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    },
    identifyBottlenecks() {
      const bottlenecks = [];
      const stages = Object.keys(this.metrics.itemsByStage);

      stages.forEach(stage => {
        const itemCount = this.metrics.itemsByStage[stage];
        const avgTime = this.metrics.avgTimeInStage[stage];
        const efficiency = this.metrics.stageMetrics.stageEfficiency[stage];

        if (efficiency < 0.5 && itemCount > 0) {
          bottlenecks.push({
            stage,
            severity: 'high',
            reason: 'Baixa eficiência no processamento',
          });
        } else if (avgTime > 7 * 24 * 60 * 60 * 1000 && itemCount > 0) {
          bottlenecks.push({
            stage,
            severity: 'medium',
            reason: 'Tempo de permanência elevado',
          });
        }
      });

      return bottlenecks;
    },
    getBottleneckSeverityClass(severity) {
      const classes = {
        high: 'bg-red-500',
        medium: 'bg-yellow-500',
        low: 'bg-orange-500',
      };
      return classes[severity] || classes.medium;
    },
    getOptimizationSuggestions() {
      const suggestions = [];
      const stages = Object.keys(this.metrics.itemsByStage);

      // Análise de distribuição de trabalho
      const avgItemsPerStage =
        stages.length > 0 ? this.metrics.totalItems / stages.length : 0;
      stages.forEach(stage => {
        const items = this.metrics.itemsByStage[stage];
        if (items > avgItemsPerStage * 1.5) {
          suggestions.push({
            icon: 'i-ph-warning-circle',
            title: `Distribuição desbalanceada em ${stage}`,
            description:
              'Considere redistribuir o trabalho ou aumentar a capacidade neste estágio',
          });
        }
      });

      // Análise de eficiência
      const lowEfficiencyStages = stages.filter(
        stage => (this.metrics.stageMetrics.stageEfficiency[stage] || 0) < 0.6
      );
      if (lowEfficiencyStages.length > 0) {
        suggestions.push({
          icon: 'i-ph-trend-up',
          title: 'Oportunidade de melhoria na eficiência',
          description:
            'Alguns estágios apresentam baixa eficiência. Analise os processos e recursos',
        });
      }

      // Análise de valor
      const avgValue = this.metrics.averageValue;
      const lowValueStages = stages.filter(
        stage =>
          (this.metrics.stageMetrics.valueByStage[stage] || 0) < avgValue * 0.5
      );
      if (lowValueStages.length > 0) {
        suggestions.push({
          icon: 'i-ph-currency-circle-dollar',
          title: 'Otimização de valor por estágio',
          description:
            'Existem estágios com valor abaixo da média. Considere priorizar itens de maior valor',
        });
      }

      return suggestions;
    },
    getAverageCycleTime() {
      const times = Object.values(this.metrics.avgTimeInStage);
      return times.length > 0
        ? times.reduce((a, b) => a + (b || 0), 0) / times.length
        : 0;
    },
    getWeeklyThroughput() {
      return this.metrics.stageMetrics.itemsCreatedThisWeek;
    },
    getPriorityColor(priority) {
      const colors = {
        low: '#94a3b8',
        medium: '#f59e0b',
        high: '#ef4444',
      };
      return colors[priority] || '#94a3b8';
    },
    formatActivityType(type) {
      const typeMap = {
        stage_changed: 'Mudança de Estágio',
        value_changed: 'Alteração de Valor',
        agent_changed: 'Mudança de Agente',
        note_added: 'Nota Adicionada',
        attachment_added: 'Anexo Adicionado',
        checklist_item_added: 'Item de Checklist Adicionado',
        checklist_item_toggled: 'Item de Checklist Alterado',
        conversation_linked: 'Conversa Vinculada',
      };
      return typeMap[type] || type;
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <ReportHeader header-title="Relatórios Kanban">
      <V4Button
        label="Baixar Relatório"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadReports"
      />
    </ReportHeader>

    <div class="flex flex-col gap-3">
      <ReportFilterSelector
        :showFunnelFilter="true"
        :showAgentsFilter="true"
        :showInboxFilter="false"
        :show-business-hours-switch="false"
        @filter-change="onFilterChange"
      />

      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center p-8">
        <div class="loading-spinner">
          <i class="i-ph-circle-notch text-2xl animate-spin text-blue-500" />
          <span class="ml-3 text-slate-600">Carregando métricas...</span>
        </div>
      </div>

      <!-- Placeholder quando não há dados para o agente selecionado -->
      <div
        v-if="
          !isLoading &&
          Object.keys(metrics.itemsByStage || {}).length === 0 &&
          userIds &&
          userIds.length > 0
        "
        class="flex items-center justify-center p-12"
      >
        <div class="text-center">
          <i class="i-ph-user-circle text-6xl text-slate-300 mb-4" />
          <h3 class="text-xl font-medium text-slate-600 mb-2">
            Nenhum item encontrado
          </h3>
          <p class="text-slate-500">
            O agente selecionado não possui itens vinculados no período
            selecionado.
          </p>
          <p class="text-sm text-slate-400 mt-2">
            Tente alterar o filtro de data ou selecionar outro agente.
          </p>
        </div>
      </div>

      <!-- Cards de métricas principais -->
      <div
        v-if="!isLoading && Object.keys(metrics.itemsByStage || {}).length > 0"
        class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6"
      >
        <!-- Resumo Geral -->
        <div class="report-card">
          <div class="card-header">
            <h3 class="flex items-center gap-2">
              <i class="i-ph-chart-bar text-blue-500" />
              Resumo Geral
            </h3>
          </div>
          <div class="card-body p-4 space-y-2">
            <div class="metric-summary-card">
              <span class="metric-label">Itens Totais</span>
              <span class="metric-value">{{
                metrics.totalItems.toLocaleString()
              }}</span>
            </div>
            <div class="metric-summary-card">
              <span class="metric-label">Valor Total</span>
              <span class="metric-value">{{
                formatCurrency(metrics.totalValue)
              }}</span>
            </div>
            <div class="metric-summary-card">
              <span class="metric-label">Valor Médio</span>
              <span class="metric-value">{{
                formatCurrency(metrics.averageValue)
              }}</span>
            </div>
          </div>
        </div>
        <!-- Criação de Itens -->
        <div class="report-card">
          <div class="card-header">
            <h3 class="flex items-center gap-2">
              <i class="i-ph-calendar-plus text-emerald-500" />
              Criação de Itens
            </h3>
          </div>
          <div class="card-body p-4 space-y-2">
            <div class="metric-summary-card">
              <span class="metric-label">Hoje</span>
              <span class="metric-value">{{
                metrics.stageMetrics.itemsCreatedToday.toLocaleString()
              }}</span>
            </div>
            <div class="metric-summary-card">
              <span class="metric-label">Esta Semana</span>
              <span class="metric-value">{{
                metrics.stageMetrics.itemsCreatedThisWeek.toLocaleString()
              }}</span>
            </div>
            <div class="metric-summary-card">
              <span class="metric-label">Este Mês</span>
              <span class="metric-value">{{
                metrics.stageMetrics.itemsCreatedThisMonth.toLocaleString()
              }}</span>
            </div>
          </div>
        </div>
        <!-- Contatos e Conversas -->
        <div class="report-card">
          <div class="card-header">
            <h3 class="flex items-center gap-2">
              <i class="i-ph-users text-indigo-500" />
              Contatos e Conversas
            </h3>
          </div>
          <div class="card-body p-4 space-y-2">
            <div class="metric-summary-card">
              <span class="metric-label">Total de Contatos</span>
              <span class="metric-value">{{
                metrics.contactMetrics.totalContacts.toLocaleString()
              }}</span>
            </div>
            <div class="metric-summary-card">
              <span class="metric-label">Conversas</span>
              <span class="metric-value">{{
                metrics.activityMetrics.itemsWithConversations.toLocaleString()
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Conteúdo dos gráficos apenas quando não estiver carregando e houver dados -->
      <div
        v-if="!isLoading && Object.keys(metrics.itemsByStage || {}).length > 0"
        class="flex flex-col space-y-6"
      >
        <!-- Container vertical para as linhas de cards -->
        <div class="flex flex-col space-y-6">
          <!-- Primeira linha: Distribuição por Etapa | Distribuição por Prioridade -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-funnel text-blue-500" />
                  Distribuição por Etapa
                </h3>
              </div>
              <div class="card-body p-4">
                <StageDistributionChart
                  :labels="Object.keys(metrics.itemsByStage || {})"
                  :values="Object.values(metrics.itemsByStage || {})"
                  :colors="
                    Object.keys(metrics.itemsByStage || {}).map(
                      stage =>
                        metrics.funnelMetrics?.stageColors?.[stage] || '#94a3b8'
                    )
                  "
                />
              </div>
            </div>
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-flag text-rose-500" />
                  Distribuição por Prioridade
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="space-y-4">
                  <div
                    v-for="(count, priority) in metrics.stageMetrics
                      ?.priorityDistribution || {}"
                    :key="priority"
                    class="priority-bar"
                  >
                    <div class="flex justify-between mb-1">
                      <span class="text-sm capitalize text-slate-600">{{
                        priority === 'low'
                          ? 'Baixa'
                          : priority === 'medium'
                          ? 'Média'
                          : 'Alta'
                      }}</span>
                      <span class="text-sm font-medium">{{ count }}</span>
                    </div>
                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full rounded-full transition-all duration-500"
                        :style="{
                          width: `${(count / metrics.totalItems) * 100}%`,
                          backgroundColor: getPriorityColor(priority),
                        }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Segunda linha: Principais Agentes | Itens com Deadline | Itens com Ofertas -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-users-three text-amber-500" />
                  Principais Agentes
                </h3>
              </div>
              <div class="card-body p-4">
                <TopAgentsList :agents="topAgents" />
              </div>
            </div>
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-calendar-clock text-blue-500" />
                  Itens com Deadline
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="flex flex-col items-center justify-center h-full">
                  <span class="text-3xl font-bold">{{
                    deadlineCardData.total
                  }}</span>
                  <span class="text-sm text-slate-500 mt-1"
                    >Itens com prazo definido</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >{{ deadlineCardData.percent }}% do total</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Valor total:
                    {{ formatCurrency(deadlineCardData.totalValue) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Valor médio:
                    {{ formatCurrency(deadlineCardData.avgValue) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Próximo deadline: {{ deadlineCardData.nextDeadline }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Prioridade mais comum:
                    {{ deadlineCardData.mostCommonPriority }}</span
                  >
                </div>
              </div>
            </div>
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-handshake text-emerald-500" />
                  Itens com Ofertas
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="flex flex-col items-center justify-center h-full">
                  <span class="text-3xl font-bold">{{
                    offersCardData.total
                  }}</span>
                  <span class="text-sm text-slate-500 mt-1"
                    >Itens com pelo menos uma oferta</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >{{ offersCardData.percent }}% do total</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Valor médio:
                    {{ formatCurrency(offersCardData.avgOfferValue) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Valor total:
                    {{ formatCurrency(offersCardData.totalOffersValue) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Maior oferta:
                    {{ formatCurrency(offersCardData.maxOffer) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Menor oferta:
                    {{ formatCurrency(offersCardData.minOffer) }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Itens com múltiplas ofertas:
                    {{ offersCardData.multiOfferCount }}</span
                  >
                  <span class="text-xs text-slate-400 mt-1"
                    >Prioridade mais comum:
                    {{ deadlineCardData.mostCommonPriority }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Terceira linha: Métricas de Ofertas | Métricas de Checklist | Métricas de Atividade -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Métricas de Ofertas -->
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-handshake text-emerald-500" />
                  Métricas de Ofertas
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="space-y-4">
                  <div class="metric-summary-card">
                    <span class="metric-label">Itens com Ofertas</span>
                    <span class="metric-value">{{ offersCardData.total }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Total de Ofertas</span>
                    <span class="metric-value">{{
                      formatCurrency(offersCardData.totalOffersValue)
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Valor Médio</span>
                    <span class="metric-value">{{
                      formatCurrency(offersCardData.avgOfferValue)
                    }}</span>
                  </div>
                  <h4
                    class="text-sm font-medium text-slate-600 dark:text-slate-300"
                  >
                    Distribuição por Valor
                  </h4>
                  <div class="offer-range-bar">
                    <div class="flex justify-between mb-1">
                      <span class="text-sm text-slate-600">Até R$ 1.000</span>
                      <span class="text-sm font-medium">{{
                        metrics.stageMetrics.offerRanges.low
                      }}</span>
                    </div>
                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-emerald-400 rounded-full transition-all duration-500"
                        :style="{
                          width: `${
                            (metrics.stageMetrics.offerRanges.low /
                              metrics.stageMetrics.totalOffers) *
                            100
                          }%`,
                        }"
                      />
                    </div>
                  </div>
                  <div class="offer-range-bar">
                    <div class="flex justify-between mb-1">
                      <span class="text-sm text-slate-600"
                        >R$ 1.001 a R$ 5.000</span
                      >
                      <span class="text-sm font-medium">{{
                        metrics.stageMetrics.offerRanges.medium
                      }}</span>
                    </div>
                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        :style="{
                          width: `${
                            (metrics.stageMetrics.offerRanges.medium /
                              metrics.stageMetrics.totalOffers) *
                            100
                          }%`,
                        }"
                      />
                    </div>
                  </div>
                  <div class="offer-range-bar">
                    <div class="flex justify-between mb-1">
                      <span class="text-sm text-slate-600"
                        >Acima de R$ 5.000</span
                      >
                      <span class="text-sm font-medium">{{
                        metrics.stageMetrics.offerRanges.high
                      }}</span>
                    </div>
                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-emerald-600 rounded-full transition-all duration-500"
                        :style="{
                          width: `${
                            (metrics.stageMetrics.offerRanges.high /
                              metrics.stageMetrics.totalOffers) *
                            100
                          }%`,
                        }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Métricas de Checklist -->
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-check-square text-indigo-500" />
                  Métricas de Checklist
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="space-y-4">
                  <div class="grid grid-cols-2 gap-4">
                    <div class="metric-summary-card">
                      <span class="metric-label">Total de Tarefas</span>
                      <span class="metric-value">{{
                        metrics.checklistMetrics.totalTasks
                      }}</span>
                    </div>
                    <div class="metric-summary-card">
                      <span class="metric-label">Tarefas Concluídas</span>
                      <span class="metric-value">{{
                        metrics.checklistMetrics.completedTasks
                      }}</span>
                    </div>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Taxa de Conclusão</span>
                    <span class="metric-value">{{
                      formatPercentage(metrics.checklistMetrics.completionRate)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Métricas de Atividade -->
            <div class="report-card">
              <div class="card-header">
                <h3 class="flex items-center gap-2">
                  <i class="i-ph-activity text-cyan-500" />
                  Métricas de Atividade
                </h3>
              </div>
              <div class="card-body p-4">
                <div class="space-y-4">
                  <div class="metric-summary-card">
                    <span class="metric-label">Total de Atividades</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.totalActivities
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Média por Item</span>
                    <span class="metric-value">{{
                      Number(
                        metrics.activityMetrics.averageActivitiesPerItem
                      ).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Com Notas</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.itemsWithNotes
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Com Anexos</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.itemsWithAttachments
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Mudanças de Estágio</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.stageChanges
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Mudanças de Valor</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.valueChanges
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Mudanças de Agente</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.agentChanges
                    }}</span>
                  </div>
                  <div class="metric-summary-card">
                    <span class="metric-label">Com Conversas</span>
                    <span class="metric-value">{{
                      metrics.activityMetrics.itemsWithConversations
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.report-card {
  @apply bg-white dark:bg-slate-800 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md border border-slate-100 dark:border-slate-700 overflow-hidden;
}

.card-header {
  @apply p-4 border-b border-slate-100 dark:border-slate-700;
}

.card-header h3 {
  @apply text-base font-medium text-slate-700 dark:text-slate-200;
}

.card-body {
  @apply p-4;
}

.metric-summary-card {
  @apply bg-slate-50 dark:bg-slate-700 rounded-lg p-3 flex flex-col items-center transition-all duration-200;
}

.metric-summary-card .metric-label {
  @apply text-xs text-slate-500 dark:text-slate-400 mb-1 text-center;
}

.metric-summary-card .metric-value {
  @apply text-lg font-semibold text-slate-900 dark:text-white;
}

.priority-bar,
.offer-range-bar {
  @apply transition-all duration-200;
}

.loading-spinner {
  @apply flex items-center justify-center;
}
</style>
